import { useContext, useState } from 'react';
import { ContentContext } from '../../contexts/ContentContext';
import useContent from '../../hooks/useContent';
import useUnsavedChanges from '../../hooks/useUnsavedChanges';
import AdminTopbar from '../AdminTopbar';
import SaveBar from '../components/SaveBar';
import ConfirmModal from '../components/ConfirmModal';

export default function PricingEditor() {
  const { content, updateSection } = useContext(ContentContext);
  const { draft, isDirty, saving, lastSaved, update, save, discard } =
    useContent(content.pricing);
  useUnsavedChanges(isDirty);

  const [deleteTier, setDeleteTier] = useState(null);
  const [activeTab, setActiveTab]   = useState('tiers');

  const handleSave    = () => save(patch => updateSection('pricing', patch));
  const handleDiscard = () => discard(content.pricing);

  const tiers = draft.tiers || [];

  const updateTier = (i, patch) =>
    update({ tiers: tiers.map((t, idx) => idx === i ? { ...t, ...patch } : t) });

  const addTier = () =>
    update({ tiers: [...tiers, { name: 'New Plan', amount: '$0 CAD', period: '/month', features: [], featured: false, ctaLabel: 'Get Started', ctaUrl: '/contact' }] });

  return (
    <div className="adm-page">
      <AdminTopbar title="Pricing" />
      <SaveBar isDirty={isDirty} saving={saving} lastSaved={lastSaved}
               onSave={handleSave} onDiscard={handleDiscard} />

      {deleteTier !== null && (
        <ConfirmModal
          message={`Delete plan "${tiers[deleteTier]?.name}"?`}
          onConfirm={() => { update({ tiers: tiers.filter((_, i) => i !== deleteTier) }); setDeleteTier(null); }}
          onCancel={() => setDeleteTier(null)}
        />
      )}

      <div className="adm-editor">
        <section className="adm-section">
          <h3 className="adm-section__title">Section Heading</h3>
          <div className="adm-field">
            <label className="adm-label">Heading</label>
            <input className="adm-input" value={draft.heading || ''}
              onChange={e => update({ heading: e.target.value })} />
          </div>
          <div className="adm-field">
            <label className="adm-label">Subtext</label>
            <textarea className="adm-textarea" rows={2} value={draft.subtext || ''}
              onChange={e => update({ subtext: e.target.value })} />
          </div>
        </section>

        <div className="adm-tabs">
          {['tiers', 'copy'].map(t => (
            <button key={t} className={`adm-tab${activeTab === t ? ' adm-tab--active' : ''}`}
              onClick={() => setActiveTab(t)}>
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>

        {activeTab === 'tiers' && (
          <section className="adm-section">
            <div className="adm-card-list">
              {tiers.map((tier, i) => (
                <div key={i} className="adm-card-item">
                  <div className="adm-card-item__header">
                    <span className="adm-label" style={{ flex: 1 }}>{tier.name || `Plan ${i + 1}`}</span>
                    <button className="adm-btn adm-btn--danger adm-btn--sm"
                      onClick={() => setDeleteTier(i)}>✕</button>
                  </div>

                  <div className="adm-row-2">
                    <div className="adm-field">
                      <label className="adm-label">Plan Name</label>
                      <input className="adm-input" value={tier.name || ''}
                        onChange={e => updateTier(i, { name: e.target.value })} />
                    </div>
                    <div className="adm-field">
                      <label className="adm-label">Badge (optional)</label>
                      <input className="adm-input" placeholder="Most Popular"
                        value={tier.badge || ''}
                        onChange={e => updateTier(i, { badge: e.target.value })} />
                    </div>
                  </div>

                  <div className="adm-row-2">
                    <div className="adm-field">
                      <label className="adm-label">Price</label>
                      <input className="adm-input" placeholder="$29 CAD"
                        value={tier.amount || ''}
                        onChange={e => updateTier(i, { amount: e.target.value })} />
                    </div>
                    <div className="adm-field">
                      <label className="adm-label">Period</label>
                      <input className="adm-input" placeholder="/month"
                        value={tier.period || ''}
                        onChange={e => updateTier(i, { period: e.target.value })} />
                    </div>
                  </div>

                  <div className="adm-field">
                    <label className="adm-label">Description</label>
                    <input className="adm-input" value={tier.desc || ''}
                      onChange={e => updateTier(i, { desc: e.target.value })} />
                  </div>

                  <div className="adm-field">
                    <label className="adm-label">Features (one per line)</label>
                    <textarea className="adm-textarea" rows={6}
                      value={(tier.features || []).join('\n')}
                      onChange={e => updateTier(i, { features: e.target.value.split('\n') })} />
                  </div>

                  <div className="adm-row-2">
                    <div className="adm-field">
                      <label className="adm-label">CTA Label</label>
                      <input className="adm-input" value={tier.ctaLabel || ''}
                        onChange={e => updateTier(i, { ctaLabel: e.target.value })} />
                    </div>
                    <div className="adm-field">
                      <label className="adm-label">CTA URL</label>
                      <input className="adm-input" value={tier.ctaUrl || ''}
                        onChange={e => updateTier(i, { ctaUrl: e.target.value })} />
                    </div>
                  </div>

                  <div className="adm-row-2">
                    <div className="adm-field adm-field--checkbox">
                      <label className="adm-label">
                        <input type="checkbox" checked={!!tier.featured}
                          onChange={e => updateTier(i, { featured: e.target.checked })} />
                        {' '}Featured (highlighted)
                      </label>
                    </div>
                  </div>

                  <div className="adm-field">
                    <label className="adm-label">Footnote</label>
                    <input className="adm-input" value={tier.footnote || ''}
                      onChange={e => updateTier(i, { footnote: e.target.value })} />
                  </div>
                </div>
              ))}
            </div>
            <button className="adm-btn adm-btn--ghost" onClick={addTier}>+ Add Plan</button>
          </section>
        )}

        {activeTab === 'copy' && (
          <section className="adm-section">
            <div className="adm-field">
              <label className="adm-label">Beta Offer Title</label>
              <input className="adm-input" value={draft.betaTitle || ''}
                onChange={e => update({ betaTitle: e.target.value })} />
            </div>
            <div className="adm-field">
              <label className="adm-label">Beta Offer Body</label>
              <textarea className="adm-textarea" rows={3} value={draft.betaBody || ''}
                onChange={e => update({ betaBody: e.target.value })} />
            </div>
            <div className="adm-field">
              <label className="adm-label">CTA Banner Headline</label>
              <input className="adm-input" value={draft.ctaHeadline || ''}
                onChange={e => update({ ctaHeadline: e.target.value })} />
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
